import React, { useEffect, useState } from "react";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { Editor } from "primereact/editor";
import { useTranslation } from "react-i18next";
import { Row, Col } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useSmartBack } from "hooks/useSmartBack";
import AITranslateButton from "components/common/AITranslateButton";
import CustomField from "components/form/CustomField";
import Switch from "components/common/Switch";
import { useDispatch, useSelector } from "react-redux";
import {
  getPageContentByType,
  updatePageContentByType,
} from "store/slices/pageContentSlice";
import { handleSubmitForm } from "helpers/apiUtils";
import { getFaqValidationSchema } from "utils/validations";
import { Icons } from "assets/icons";
import PageHeader from "components/common/PageHeader";

const PAGE_CONTENT_META = {
  TermsAndConditions: {
    title: "Terms & Conditions",
    subTitle: "Create and update the terms and conditions for app users",
  },
  PrivacyPolicy: {
    title: "Privacy Policy",
    subTitle: "Create and update the privacy policy for app users",
  },
  FAQ: {
    title: "FAQ",
    subTitle: "Create and update the FAQ for app users",
  },
};

const EMPTY_FAQ = {
  question: "",
  questionAr: "",
  answer: "",
  answerAr: "",
  status: true,
};

const PageContentEditor = ({ defaultType }) => {
  const { t } = useTranslation();
  const goBack = useSmartBack();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { trash, plus } = Icons || {};

  const { data, isLoading } = useSelector((state) => state.PageContent);

  const CONTENT_TYPE = location.state?.contentType || defaultType;
  const meta = PAGE_CONTENT_META[CONTENT_TYPE] || {};
  const isFAQ = CONTENT_TYPE === "FAQ";

  // ✅ Key trick: forces Editor to remount once real data is available
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (CONTENT_TYPE) {
      setIsDataLoaded(false); // reset on type change
      dispatch(getPageContentByType({ type: CONTENT_TYPE }));
    }
  }, [CONTENT_TYPE]);

  const [initialValues, setInitialValues] = useState({
    descriptionEn: "",
    descriptionAr: "",
    faqs: [EMPTY_FAQ],
  });

  useEffect(() => {
    if (data) {
      if (isFAQ) {
        setInitialValues({
          faqs: data.faqs?.length
            ? data.faqs
                .slice()
                .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                .map((faq) => ({
                  question: faq.question || "",
                  questionAr: faq.questionAr || "",
                  answer: faq.answer || "",
                  answerAr: faq.answerAr || "",
                  status: faq.status !== false,
                }))
            : [EMPTY_FAQ],
        });
      } else {
        setInitialValues({
          descriptionEn: data.description || "",
          descriptionAr: data.descriptionAr || "",
        });
      }
      setIsDataLoaded(true); // ✅ triggers Editor remount with populated values
    }
  }, [data, isFAQ]);

  const validationSchema = isFAQ
    ? getFaqValidationSchema(t)
    : Yup.object({
        descriptionEn: Yup.string().required(t("English description is required")),
        descriptionAr: Yup.string().required(t("Arabic description is required")),
      });

  const handleSubmit = (values) => {
    const payload = isFAQ
      ? {
          type: CONTENT_TYPE,
          faqs: values.faqs.map((faq, index) => ({
            ...faq,
            sortOrder: index + 1,
          })),
        }
      : {
          type: CONTENT_TYPE,
          description: values.descriptionEn,
          descriptionAr: values.descriptionAr,
        };

    handleSubmitForm({
      dispatch,
      action: updatePageContentByType,
      payload,
      onSuccess: () => navigate(-1),
    });
  };

  return (
    <div className="page-content term_condition_page add_update_faq_page">
      <PageHeader title={t(meta.title)} subTitle={t(meta.subTitle)} />

      <div className="common_card">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              {isFAQ ? (
                <FieldArray name="faqs">
                  {({ push, remove }) => (
                    <>
                      {values.faqs.map((faq, index) => {
                        return (
                          <div key={index} className="faq_repeater_card mb-3">
                            <div className="faq_repeater_card_header">
                              <span className="faq_repeater_card_title">
                                {t("FAQ")} {index + 1}
                              </span>
                              <div className="d-flex align-items-center gap-3">
                                <Switch
                                  label={t("Status")}
                                  checked={faq.status}
                                  handleOnChange={(e) =>
                                    setFieldValue(
                                      `faqs[${index}].status`,
                                      e.value,
                                    )
                                  }
                                />
                                {values.faqs.length > 1 && (
                                  <img
                                    src={trash}
                                    alt="trash"
                                    width={20}
                                    height={20}
                                    className="c_p"
                                    onClick={() => remove(index)}
                                  />
                                )}
                              </div>
                            </div>

                            <Row className="g-3 mt-1">
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomField
                                    label={t("Question (English)")}
                                    name={`faqs[${index}].question`}
                                    placeholder={t("Enter question")}
                                    required
                                    enableTranslate
                                    translateTo="ar"
                                    targetField={`faqs[${index}].questionAr`}
                                  />
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomField
                                    label={t("Question (Arabic)")}
                                    name={`faqs[${index}].questionAr`}
                                    placeholder={t("Enter question")}
                                    className="rtl"
                                    required
                                    enableTranslate
                                    translateTo="en"
                                    targetField={`faqs[${index}].question`}
                                  />
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomField
                                    as="textarea"
                                    label={t("Answer (English)")}
                                    name={`faqs[${index}].answer`}
                                    placeholder={t("Enter answer")}
                                    required
                                    enableTranslate
                                    translateTo="ar"
                                    targetField={`faqs[${index}].answerAr`}
                                  />
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomField
                                    as="textarea"
                                    label={t("Answer (Arabic)")}
                                    name={`faqs[${index}].answerAr`}
                                    placeholder={t("Enter answer")}
                                    className="rtl"
                                    required
                                    enableTranslate
                                    translateTo="en"
                                    targetField={`faqs[${index}].answer`}
                                  />
                                </div>
                              </Col>
                            </Row>
                          </div>
                        );
                      })}

                      <button
                        type="button"
                        className="main_btn outline d-flex align-items-center gap-1 mb-4"
                        onClick={() => push({ ...EMPTY_FAQ })}
                      >
                        {t("Add FAQ")}
                      </button>
                    </>
                  )}
                </FieldArray>
              ) : (
                <Row className="g-4">
                  {/* English Description */}
                  <Col lg={6}>
                    <div className="form_field position-relative">
                      <AITranslateButton
                        sourceField="descriptionEn"
                        targetField="descriptionAr"
                        translateTo="ar"
                      />
                      <label>{t("(English) Description")}</label>
                      <Editor
                        key={`en-${isDataLoaded}`} // ✅
                        value={values.descriptionEn}
                        onTextChange={(e) =>
                          setFieldValue("descriptionEn", e.htmlValue)
                        }
                        style={{ height: "400px" }}
                      />
                      {errors.descriptionEn && touched.descriptionEn && (
                        <div className="text-danger">{errors.descriptionEn}</div>
                      )}
                    </div>
                  </Col>

                  {/* Arabic Description */}
                  <Col lg={6}>
                    <div className="form_field position-relative">
                      <AITranslateButton
                        sourceField="descriptionAr"
                        targetField="descriptionEn"
                        translateTo="en"
                      />
                      <label>{t("(Arabic) Description")}</label>
                      <Editor
                        key={`ar-${isDataLoaded}`} // ✅
                        value={values.descriptionAr}
                        onTextChange={(e) =>
                          setFieldValue("descriptionAr", e.htmlValue)
                        }
                        style={{ height: "400px" }}
                        className="rtl_editor"
                      />
                      {errors.descriptionAr && touched.descriptionAr && (
                        <div className="text-danger">{errors.descriptionAr}</div>
                      )}
                    </div>
                  </Col>
                </Row>
              )}

              {/* Buttons */}
              <Row>
                <Col lg={12} className="mt-3">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="main_btn outline"
                      onClick={goBack}
                      disabled={isLoading}
                    >
                      {t("Cancel")}
                    </button>
                    <button
                      type="submit"
                      className="main_btn"
                      disabled={isLoading}
                    >
                      {isLoading ? t("Saving...") : t("Save")}
                    </button>
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default PageContentEditor;
